import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Divider,
  InputAdornment,
  alpha,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import {
  createFunderRate,
  updateFunderRate,
} from "../../../api/services/Billing/funderRates";
import { getFunders } from "../../../api/services/Billing/funder";
import CustomInput from "../../FormFields/CustomInput";
import CustomSelect from "../../FormFields/CustomSelect/CustomSelect";
import CustomCheckbox from "../../CustomForm/CustomCheckbox";

const SERVICE_OPTIONS = [
  { label: "Personal Care (30 min)", code: "PC30", type: "hourly", mins: 30 },
  { label: "Personal Care (45 min)", code: "PC45", type: "hourly", mins: 45 },
  { label: "Personal Care (60 min)", code: "PC60", type: "hourly", mins: 60 },
  { label: "Sleep-In", code: "SLEEP01", type: "flat", mins: 480 },
  { label: "Waking Night", code: "WAKE01", type: "hourly", mins: 60 },
  { label: "Domestic Support", code: "DOM01", type: "hourly", mins: 60 },
  { label: "Live-in Care (Daily)", code: "LIVE01", type: "flat", mins: 1440 },
  { label: "Social Support", code: "SOC01", type: "hourly", mins: 60 },
];

const RATE_TYPE_OPTIONS = [
  { value: "hourly", label: "Hourly" },
  { value: "flat", label: "Flat Rate" },
];

const BH_PRICING_OPTIONS = [
  { value: "multiplier", label: "Multiplier" },
  { value: "override", label: "Fixed Rate" },
];

const SectionDivider = ({ icon: Icon, label, color = "#2469F0" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 1 }}>
    <Icon sx={{ fontSize: 16, color }} />
    <Typography
      variant="caption"
      fontWeight={700}
      color="#475467"
      sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
    >
      {label}
    </Typography>
    <Divider sx={{ flex: 1 }} />
  </Box>
);

const FunderRateDialog = ({ open, handleClose, editData = null, funderId = null }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [useDurationRates, setUseDurationRates] = useState(false);
  const [durationRates, setDurationRates] = useState([
    { start_min: 0, end_min: 15, rate: "" },
    { start_min: 16, end_min: 30, rate: "" },
    { start_min: 31, end_min: 45, rate: "" },
    { start_min: 46, end_min: 60, rate: "" },
  ]);

  const [formData, setFormData] = useState({
    funder: "",
    service_name: "",
    service_code: "",
    rate_type: "hourly",
    unit_minutes: 60,
    weekday_rate: "",
    weekend_rate: "",
    bank_holiday_pricing_type: "multiplier",
    bank_holiday_multiplier: "2.0",
    bank_holiday_rate: "",
    mileage_rate: "0.00",
  });

  const { data: funders = [] } = useQuery(
    ["funders", "COUNCIL"],
    () => getFunders({ category: "COUNCIL" }),
    { enabled: open }
  );

  const funderOptions = funders.map((f) => ({ value: f.id, label: f.name }));
  const serviceOptions = SERVICE_OPTIONS.map((s) => ({
    value: s.label,
    label: s.label,
  }));

  useEffect(() => {
    if (editData) {
      setFormData(editData);
      if (editData.duration_rates && editData.duration_rates.length > 0) {
        setDurationRates(editData.duration_rates);
        setUseDurationRates(true);
      } else {
        setUseDurationRates(false);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        funder: funderId || "",
        service_name: "",
        service_code: "",
        weekday_rate: "",
        weekend_rate: "",
        bank_holiday_pricing_type: "multiplier",
        bank_holiday_multiplier: "2.0",
        mileage_rate: "0.00",
      }));
      setUseDurationRates(false);
    }
  }, [editData, open, funderId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "service_name") {
      const selectedService = SERVICE_OPTIONS.find((s) => s.label === value);
      if (selectedService) {
        setFormData((prev) => ({
          ...prev,
          service_name: value,
          service_code: selectedService.code,
          rate_type: selectedService.type,
          unit_minutes: selectedService.mins,
        }));
        return;
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDurationChange = (index, field, value) => {
    const newRates = [...durationRates];
    newRates[index][field] = value;
    setDurationRates(newRates);
  };

  const addDurationRow = () => {
    const lastRow = durationRates[durationRates.length - 1];
    const nextStart = lastRow ? parseInt(lastRow.end_min || 0) + 1 : 0;
    setDurationRates([
      ...durationRates,
      { start_min: nextStart, end_min: "", rate: "" },
    ]);
  };

  const removeDurationRow = (index) => {
    setDurationRates(durationRates.filter((_, i) => i !== index));
  };

  const mutation = useMutation(
    (data) =>
      editData ? updateFunderRate(editData.id, data) : createFunderRate(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("funders");
        enqueueSnackbar("Rate Card Saved Successfully", { variant: "success" });
        handleClose();
      },
      onError: () => enqueueSnackbar("Error saving rate", { variant: "error" }),
    }
  );

  const handleSave = () => {
    const submissionData = { ...formData };
    submissionData.duration_rates = useDurationRates ? durationRates : [];
    mutation.mutate(submissionData);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: alpha("#F59E0B", 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MoneyIcon sx={{ color: "#F59E0B", fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
              {editData ? "View/Edit Rate Card Details" : "New Rate Card Entry"}
            </Typography>
            <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
              Configure service rates for funders
            </Typography>
          </Box>
        </Stack>
        <Chip
          label={formData.rate_type === "hourly" ? "Hourly" : "Flat"}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 600,
            bgcolor: alpha("#2469F0", 0.08),
            color: "#2469F0",
          }}
        />
      </Box>

      {/* CONTENT */}
      <DialogContent sx={{ pt: 3, px: 2.5 }}>
        <Stack spacing={2}>
          {/* SERVICE DETAILS */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 48%", minWidth: "200px" }}>
              <CustomSelect
                label="Council / Funder *"
                name="funder"
                options={funderOptions}
                value={formData.funder}
                onChange={handleChange}
                placeholder="Select funder"
                fullWidth
                searchable
              />
            </Box>
            <Box sx={{ flex: "1 1 48%", minWidth: "200px" }}>
              <CustomSelect
                label="Service Name *"
                name="service_name"
                options={serviceOptions}
                value={formData.service_name}
                onChange={handleChange}
                placeholder="Select service"
                fullWidth
                searchable
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 22%", minWidth: "150px" }}>
              <CustomInput
                label="Service Code"
                name="service_code"
                value={formData.service_code}
                size="medium"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box sx={{ flex: "1 1 22%", minWidth: "150px" }}>
              <CustomSelect
                label="Rate Type"
                name="rate_type"
                options={RATE_TYPE_OPTIONS}
                value={formData.rate_type}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 22%", minWidth: "150px" }}>
              <CustomInput
                label="Unit (mins)"
                name="unit_minutes"
                type="number"
                value={formData.unit_minutes}
                onChange={handleChange}
                size="medium"
                fullWidth
              />
            </Box>
          </Box>

          {/* STANDARD RATES */}
          <SectionDivider icon={MoneyIcon} label="Standard Rates" />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 48%", minWidth: "200px" }}>
              <CustomInput
                label="Standard Weekday Rate"
                name="weekday_rate"
                type="number"
                value={formData.weekday_rate}
                onChange={handleChange}
                size="medium"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
              />
            </Box>
            <Box sx={{ flex: "1 1 48%", minWidth: "200px" }}>
              <CustomInput
                label="Standard Weekend Rate"
                name="weekend_rate"
                type="number"
                value={formData.weekend_rate}
                onChange={handleChange}
                size="medium"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
              />
            </Box>
          </Box>

          {/* DURATION RATES TOGGLE */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: "#F8FAFC",
              borderRadius: "6px",
              border: "1px solid #E2E8F0",
            }}
          >
            <CustomCheckbox
              label="Add Fixed Rate Based on Visit Duration (Like Croydon Rates)"
              checked={useDurationRates}
              onClick={(checked) => setUseDurationRates(checked)}
            />
          </Box>

          {/* DURATION RATES TABLE */}
          {useDurationRates && (
            <Box
              sx={{
                p: 2,
                bgcolor: "#F8FAFC",
                borderRadius: "8px",
                border: "1px dashed #D0D5DD",
              }}
            >
              <SectionDivider icon={ScheduleIcon} label="Visit Duration Details" color="#F59E0B" />

              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                {durationRates.map((row, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      p: 1,
                      bgcolor: "#FFFFFF",
                      borderRadius: "6px",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <Typography variant="body2" color="#475467" sx={{ fontSize: "0.8rem" }}>
                      Lasting between
                    </Typography>
                    <Box sx={{ width: 70 }}>
                      <CustomInput
                        value={row.start_min}
                        onChange={(e) => handleDurationChange(index, "start_min", e.target.value)}
                        size="small"
                        type="number"
                      />
                    </Box>
                    <Typography variant="body2" color="#475467" sx={{ fontSize: "0.8rem" }}>
                      and
                    </Typography>
                    <Box sx={{ width: 70 }}>
                      <CustomInput
                        value={row.end_min}
                        onChange={(e) => handleDurationChange(index, "end_min", e.target.value)}
                        size="small"
                        type="number"
                      />
                    </Box>
                    <Typography variant="body2" color="#475467" sx={{ fontSize: "0.8rem" }}>
                      mins invoiced at
                    </Typography>
                    <Box sx={{ width: 120 }}>
                      <CustomInput
                        value={row.rate}
                        onChange={(e) => handleDurationChange(index, "rate", e.target.value)}
                        size="small"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">£</InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="#475467" sx={{ fontSize: "0.8rem" }}>
                      per visit
                    </Typography>
                    {index > 3 && (
                      <IconButton
                        size="small"
                        onClick={() => removeDurationRow(index)}
                        sx={{
                          color: "#EF4444",
                          p: 0.5,
                          "&:hover": { bgcolor: alpha("#EF4444", 0.04) },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>

              <Button
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                size="small"
                onClick={addDurationRow}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontSize: "0.75rem",
                  color: "#2469F0",
                  "&:hover": { bgcolor: alpha("#2469F0", 0.04) },
                }}
              >
                Add More Duration Range
              </Button>
            </Box>
          )}

          {/* BANK HOLIDAY & MILEAGE */}
          <SectionDivider icon={MoneyIcon} label="Bank Holiday & Mileage" color="#94A3B8" />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 30%", minWidth: "180px" }}>
              <CustomSelect
                label="BH Pricing"
                name="bank_holiday_pricing_type"
                options={BH_PRICING_OPTIONS}
                value={formData.bank_holiday_pricing_type}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 30%", minWidth: "180px" }}>
              {formData.bank_holiday_pricing_type === "multiplier" ? (
                <CustomInput
                  label="BH Multiplier"
                  name="bank_holiday_multiplier"
                  value={formData.bank_holiday_multiplier}
                  onChange={handleChange}
                  size="medium"
                  fullWidth
                />
              ) : (
                <CustomInput
                  label="BH Fixed Rate"
                  name="bank_holiday_rate"
                  type="number"
                  value={formData.bank_holiday_rate}
                  onChange={handleChange}
                  size="medium"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                />
              )}
            </Box>
            <Box sx={{ flex: "1 1 30%", minWidth: "180px" }}>
              <CustomInput
                label="Mileage Rate For Service"
                name="mileage_rate"
                type="number"
                value={formData.mileage_rate}
                onChange={handleChange}
                size="medium"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions sx={{ borderTop: "1px solid #E2E8F0", p: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "6px",
            borderColor: "#D0D5DD",
            color: "#344054",
            px: 3,
            py: 0.75,
            "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={mutation.isLoading || !formData.service_name}
          startIcon={
            mutation.isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon sx={{ fontSize: 18 }} />
            )
          }
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "6px",
            bgcolor: "#2469F0",
            px: 3,
            py: 0.75,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
            },
            "&.Mui-disabled": { bgcolor: "#F1F5F9", color: "#94A3B8" },
          }}
        >
          {mutation.isLoading ? "Saving..." : "Save Rate Card"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FunderRateDialog;